import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api,
  type DemoDescriptor,
  type DemoStatus,
  type StepResult,
  type WalkthroughPhase,
  type WalkthroughStep,
} from '../lib/api';
import { ThemeToggle } from '../components/ThemeToggle';
import { CountFlip } from '../components/CountFlip';
import { QosChart, type QosRun } from '../components/QosChart';
import { DemoDiagram } from '../components/DemoDiagram';

type Mode = 'run' | 'teardown';

interface LogEntry {
  key: string;
  stepId: string;
  attempt: number;
  title: string;
  phase: WalkthroughPhase;
  commands: string[];
  output: string;
  ok: boolean;
  message: string;
}

/** An auto-retry in progress for a verify-style step (ILM scanner pass, async replication). */
interface PollState {
  step: WalkthroughStep;
  attempt: number; // the attempt this countdown will fire (2..maxAttempts)
  secondsLeft: number;
}

const PHASE_LABEL: Record<WalkthroughPhase, string> = {
  BOOTH: 'Booth',
  AISTOR: 'Your AIStor',
  DATABRICKS: 'Databricks',
  IAM: 'Identity',
  ACCESS: 'Access',
  TIERING: 'Tiering',
  REPLICATION: 'Replication',
  QOS: 'QoS',
  GOVERNANCE: 'Governance',
  OBSERVABILITY: 'Observability',
};

/**
 * The dedicated, full-screen demo walkthrough — opened in its own tab. Two columns: the left drives
 * the demo (narrated, runnable steps); the right is a live operations transcript showing the actual
 * (sanitized) mc / pyiceberg / Databricks-REST commands + output as they run, for education. Backend-
 * agnostic — the same operations show whether the system is local (Podman) or on AWS (EC2). Also hosts
 * the teardown walkthrough (mode=teardown), which removes only what this demo created.
 */
export function DemoPage() {
  const params = new URLSearchParams(window.location.search);
  const systemId = params.get('system') ?? '';
  const demoId = params.get('demo') ?? '';
  const mode: Mode = params.get('mode') === 'teardown' ? 'teardown' : 'run';

  const [demo, setDemo] = useState<DemoDescriptor | null>(null);
  const [status, setStatus] = useState<DemoStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, StepResult>>({});
  const [running, setRunning] = useState<string | null>(null);
  // The last step that ran — with `running`, drives the architecture diagram's highlight.
  const [lastRun, setLastRun] = useState<string | null>(null);
  const [poll, setPoll] = useState<PollState | null>(null);
  const [log, setLog] = useState<LogEntry[]>([]);
  // Measured WARP throughput per QoS run step (parsed from run output) — feeds the comparison chart.
  const [qos, setQos] = useState<Record<string, number>>({});
  // Presentation mode: bigger type + roomier layout for screen-share/projector. Deep-linkable.
  const [present, setPresent] = useState(() => params.get('present') === '1');
  // Which pane(s) show: split (default), steps only, or terminal only.
  const [view, setView] = useState<'split' | 'steps' | 'terminal'>('split');
  const consoleRef = useRef<HTMLDivElement>(null);

  const togglePresent = () => {
    setPresent((p) => {
      const next = !p;
      const u = new URL(window.location.href);
      if (next) u.searchParams.set('present', '1');
      else u.searchParams.delete('present');
      window.history.replaceState({}, '', u);
      return next;
    });
  };

  const revalidate = useCallback(async () => {
    try {
      const { status } = await api.validateDemo(systemId, demoId);
      setStatus(status);
    } catch {
      /* keep prior status */
    }
  }, [systemId, demoId]);

  useEffect(() => {
    (async () => {
      try {
        const [{ demos }, { status }] = await Promise.all([api.demos(), api.validateDemo(systemId, demoId)]);
        const d = demos.find((x) => x.id === demoId) ?? null;
        if (!d) throw new Error('unknown demo');
        setDemo(d);
        setStatus(status);
        document.title = `${d.label} — ${mode === 'teardown' ? 'Teardown' : 'Walkthrough'}`;
      } catch (e) {
        setError(e instanceof Error ? e.message : 'failed to load');
      }
    })();
  }, [systemId, demoId, mode]);

  useEffect(() => {
    consoleRef.current?.scrollTo({ top: consoleRef.current.scrollHeight });
  }, [log]);

  const steps = demo ? (mode === 'teardown' ? demo.teardown : demo.walkthrough) : [];

  const run = useCallback(
    async (step: WalkthroughStep, attempt = 1) => {
      setRunning(step.id);
      setLastRun(step.id);
      try {
        const { result } = await api.runStep(systemId, demoId, step.id);
        setResults((r) => ({ ...r, [step.id]: result }));
        // A QoS load run reports its throughput in the output — capture the summary (last) figure.
        if (step.phase === 'QOS' && step.action?.kind === 'runner') {
          const matches = [...result.output.matchAll(/([\d.]+)\s*obj\/s/g)];
          const last = matches[matches.length - 1]?.[1];
          if (last != null) {
            const v = parseFloat(last);
            if (Number.isFinite(v)) setQos((q) => ({ ...q, [step.id]: v }));
          }
        }
        setLog((l) => {
          const entry: LogEntry = {
            key: `${step.id}-${l.length}-${attempt}`,
            stepId: step.id,
            attempt,
            title: step.title,
            phase: step.phase,
            commands: result.commands,
            output: result.output,
            ok: result.ok,
            message: result.message,
          };
          // Auto-retry attempts replace the previous check's transcript entry (same step, tail of the
          // log) instead of stacking a dozen near-identical failures while we wait for convergence.
          const last = l[l.length - 1];
          if (attempt > 1 && last && last.stepId === step.id) return [...l.slice(0, -1), entry];
          return [...l, entry];
        });
        // A verify-style step that hasn't converged yet re-arms its countdown (visible on the step).
        if (!result.ok && step.poll && attempt < step.poll.maxAttempts) {
          setPoll({ step, attempt: attempt + 1, secondsLeft: step.poll.intervalSeconds });
        }
        if (step.action && ['setup', 'publish-share', 'databricks-setup', 'databricks-teardown', 'share-teardown', 'mc-script', 'runner'].includes(step.action.kind)) {
          void revalidate();
        }
      } catch (e) {
        const msg = e instanceof Error ? e.message : 'failed';
        setLog((l) => [...l, { key: `${step.id}-${l.length}-${attempt}`, stepId: step.id, attempt, title: step.title, phase: step.phase, commands: [], output: msg, ok: false, message: msg }]);
      } finally {
        setRunning(null);
      }
    },
    [systemId, demoId, revalidate],
  );

  // Countdown driver for the (single) active auto-retry: tick once a second; at zero, fire the next
  // check — waiting out any in-flight request first (the effect re-runs when `running` clears).
  useEffect(() => {
    if (!poll) return;
    if (poll.secondsLeft <= 0) {
      if (running) return;
      const p = poll;
      setPoll(null);
      void run(p.step, p.attempt);
      return;
    }
    const t = window.setTimeout(
      () => setPoll((cur) => (cur ? { ...cur, secondsLeft: cur.secondsLeft - 1 } : cur)),
      1000,
    );
    return () => window.clearTimeout(t);
  }, [poll, running, run]);

  // A manual run of the polled step supersedes the pending auto-retry (run() re-arms it on failure).
  const runManual = useCallback(
    (step: WalkthroughStep) => {
      setPoll((cur) => (cur && cur.step.id === step.id ? null : cur));
      void run(step);
    },
    [run],
  );

  // The customer leave-behind: everything the session actually ran — commands, results, output —
  // as a Markdown file. "Here's exactly what we just did; try it yourself."
  const exportTranscript = useCallback(() => {
    if (!demo || log.length === 0) return;
    const lines: string[] = [
      `# ${demo.label} — operations transcript`,
      '',
      `- System: \`${systemId}\``,
      `- Mode: ${mode === 'teardown' ? 'teardown' : 'walkthrough'}`,
      `- Exported: ${new Date().toISOString()}`,
      '',
      'Every step below shows the actual (sanitized) operations run against the system — the same',
      'commands work from any `mc`/S3 client. Credentials were substituted at run time and never',
      'appear here.',
      '',
    ];
    for (const e of log) {
      lines.push(`## ${PHASE_LABEL[e.phase]} · ${e.title}${e.attempt > 1 ? ` (check ${e.attempt})` : ''}`);
      if (e.commands.length > 0) lines.push('', '```shell', ...e.commands, '```');
      lines.push('', `${e.ok ? '✅' : '❌'} ${e.message}`);
      if (e.output) lines.push('', '```text', e.output, '```');
      lines.push('');
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${demoId}-${mode === 'teardown' ? 'teardown' : 'walkthrough'}-transcript.md`;
    a.click();
    URL.revokeObjectURL(a.href);
  }, [demo, log, systemId, demoId, mode]);

  if (error) {
    return (
      <div className="demopage">
        <p className="error" style={{ padding: '2rem' }}>
          {error} — <a href="/">back to dashboard</a>
        </p>
      </div>
    );
  }
  if (!demo || !status) return <div className="demopage"><p className="muted" style={{ padding: '2rem' }}>Loading…</p></div>;

  const otherMode: Mode = mode === 'teardown' ? 'run' : 'teardown';
  // Warehouse/catalog/schema are Databricks (Tables + Delta Sharing) concepts — meaningless noise in
  // the header of a demo that doesn't use that integration.
  const showDatabricksMeta = (demo.requiresIntegrations ?? []).includes('databricks');
  // The QoS comparison, in walkthrough order, with only the runs measured so far.
  const qosRuns: QosRun[] = steps
    .filter((s) => s.phase === 'QOS' && s.action?.kind === 'runner')
    .flatMap((s) => {
      const v = qos[s.id];
      if (v == null) return [];
      const cap = s.title.match(/(\d+)\s*rps/i);
      return [{ id: s.id, label: cap ? `${cap[1]} rps cap` : 'Baseline', value: v, cap: cap ? Number(cap[1]) : undefined }];
    });

  return (
    <div className={`demopage ${mode === 'teardown' ? 'teardown' : ''} ${present ? 'present' : ''} view-${view}`}>
      <header className="dp-top">
        <div className="dp-top-main">
          <span className="dp-kicker">MinIO AIStor · Demo Hub{mode === 'teardown' ? ' · Teardown' : ''}</span>
          <h1>{demo.label.replace(/ —.*/, '')}</h1>
          <p className="muted dp-sub">
            {demo.label.includes('—') ? demo.label.split('—').slice(1).join('—').trim() : demo.description}
          </p>
        </div>
        <div className="dp-top-side">
          <span className={`pill ${status.available === 'available' ? 'ok' : status.available === 'unavailable' ? 'off' : 'warn'}`}>
            {status.available}
          </span>
          {showDatabricksMeta && (
            <div className="dp-meta">
              <div><span>Warehouse</span><code>{status.warehouse}</code></div>
              <div><span>Catalog</span><code>{status.databricksCatalog ?? '—'}</code></div>
              <div><span>Schema</span><code>{status.databricksSchema}</code></div>
            </div>
          )}
          <div className="dp-links">
            <div className="dp-seg" role="group" aria-label="Pane layout">
              <button type="button" className={view === 'steps' ? 'on' : ''} title="Steps only" onClick={() => setView('steps')}>
                Steps
              </button>
              <button type="button" className={view === 'split' ? 'on' : ''} title="Steps + operations" onClick={() => setView('split')}>
                Split
              </button>
              <button type="button" className={view === 'terminal' ? 'on' : ''} title="Operations only" onClick={() => setView('terminal')}>
                Ops
              </button>
            </div>
            <button
              type="button"
              className={`btn small ${present ? '' : 'ghost'}`}
              title="Bigger type and roomier layout for screen sharing / projectors"
              onClick={togglePresent}
            >
              {present ? 'Exit present' : 'Present'}
            </button>
            <ThemeToggle />
            <a className="btn ghost small" href={`/demo?system=${systemId}&demo=${demoId}&mode=${otherMode}`}>
              {mode === 'teardown' ? '← Walkthrough' : 'Teardown →'}
            </a>
            <a className="btn ghost small" href="/">Dashboard</a>
          </div>
        </div>
      </header>

      <div className="dp-body">
        <section className="dp-left">
          <ProgressRail steps={steps} results={results} running={running} pollStepId={poll?.step.id ?? null} />
          <DemoDiagram demoId={demoId} activeStepId={running ?? poll?.step.id ?? lastRun} />
          <ol className="dp-steps">
            {steps.map((step, i) => (
              <StepCard
                key={step.id}
                step={step}
                index={i + 1}
                running={running === step.id}
                anyRunning={running !== null}
                result={results[step.id]}
                waiting={poll && poll.step.id === step.id ? poll : null}
                status={status}
                qosRuns={qosRuns}
                onRun={() => runManual(step)}
                onCancelWait={() => setPoll(null)}
              />
            ))}
          </ol>
        </section>

        <section className="dp-right">
          <div className="dp-console-head">
            <span className="dot" /> operations
            <span className="muted dp-console-note">the actual commands run on your system + Databricks</span>
            <button
              type="button"
              className="dp-term-btn"
              disabled={log.length === 0}
              title="Download this session's commands + output as Markdown — the leave-behind"
              onClick={exportTranscript}
            >
              Export .md
            </button>
          </div>
          <div className="dp-console" ref={consoleRef}>
            {log.length === 0 && <div className="dp-console-empty">Run a step — the mc / pyiceberg / REST operations show here.</div>}
            {log.map((e) => (
              <div key={e.key} className={`dp-log ${e.ok ? '' : 'bad'}`}>
                <div className={`dp-log-head phase-${e.phase.toLowerCase()}`}>
                  <span className="dp-log-phase">{PHASE_LABEL[e.phase]}</span>
                  <span className="dp-log-title">
                    {e.title}
                    {e.attempt > 1 && <span className="dp-log-attempt"> · check {e.attempt}</span>}
                  </span>
                  {e.commands.length > 0 && <CopyCommands commands={e.commands} />}
                </div>
                {e.commands.map((c, j) => (
                  <div key={j} className={c.startsWith('#') ? 'dp-cmd comment' : 'dp-cmd'}>
                    {c.startsWith('#') ? c : <><span className="prompt">$</span> {c}</>}
                  </div>
                ))}
                <div className={`dp-log-msg ${e.ok ? 'ok' : 'bad'}`}>{e.ok ? '✓' : '✗'} {e.message}</div>
                {e.output && <pre className="dp-log-out">{e.output}</pre>}
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

/** Per-entry copy: puts the entry's commands on the clipboard so a customer can re-run them. */
function CopyCommands({ commands }: { commands: string[] }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(commands.join('\n'));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  };
  return (
    <button type="button" className="dp-term-btn" title="Copy this step's commands" onClick={() => void copy()}>
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

/**
 * Sticky "where are we?" strip above the steps: overall progress plus one jump-dot per step, coloured
 * by its state (done / failed / running / auto-retrying / not run yet). Gives presenter + audience a
 * constant sense of position in the story.
 */
function ProgressRail({
  steps,
  results,
  running,
  pollStepId,
}: {
  steps: WalkthroughStep[];
  results: Record<string, StepResult>;
  running: string | null;
  pollStepId: string | null;
}) {
  if (steps.length === 0) return null;
  const done = steps.filter((s) => results[s.id]?.ok).length;
  const stateOf = (s: WalkthroughStep) => {
    if (running === s.id) return 'running';
    if (pollStepId === s.id) return 'waiting';
    const r = results[s.id];
    if (!r) return 'pending';
    return r.ok ? 'done' : 'failed';
  };
  return (
    <div className="dp-progress" role="navigation" aria-label="Walkthrough progress">
      <span className="dp-progress-count">
        {done}/{steps.length} steps
      </span>
      <div className="dp-progress-dots">
        {steps.map((s, i) => (
          <button
            key={s.id}
            type="button"
            className={`dp-jump ${stateOf(s)}`}
            title={s.title}
            aria-label={`Step ${i + 1}: ${s.title}`}
            onClick={() => document.getElementById(`dp-step-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
          >
            {stateOf(s) === 'done' ? '✓' : stateOf(s) === 'failed' ? '✗' : i + 1}
          </button>
        ))}
      </div>
    </div>
  );
}

function StepCard({
  step,
  index,
  running,
  anyRunning,
  result,
  waiting,
  status,
  qosRuns,
  onRun,
  onCancelWait,
}: {
  step: WalkthroughStep;
  index: number;
  running: boolean;
  anyRunning: boolean;
  result: StepResult | undefined;
  waiting: PollState | null;
  status: DemoStatus;
  qosRuns: QosRun[];
  onRun: () => void;
  onCancelWait: () => void;
}) {
  const isCount = step.action?.kind === 'databricks-count';
  const sql =
    isCount && step.action?.kind === 'databricks-count'
      ? `SELECT COUNT(*) FROM ${status.databricksCatalog ?? '<your-catalog>'}.${status.databricksSchema}.${step.action.table};`
      : null;

  // Step badge: number until the step has run; then ✓ / ✗ (a step mid-auto-retry keeps its number —
  // it hasn't failed, it's converging).
  const state = running ? 'running' : waiting ? 'waiting' : result ? (result.ok ? 'done' : 'failed') : 'pending';
  const badge = state === 'done' ? '✓' : state === 'failed' ? '✗' : index;

  return (
    <li id={`dp-step-${step.id}`} className={`dp-step phase-${step.phase.toLowerCase()} ${state}`}>
      <div className="dp-step-head">
        <span className={`dp-num ${state}`}>{badge}</span>
        <span className="dp-step-title">{step.title}</span>
        <span className="dp-step-phase">{PHASE_LABEL[step.phase]}</span>
      </div>
      <div className="dp-narrative" dangerouslySetInnerHTML={{ __html: step.narrativeHtml }} />

      {isCount && step.action?.kind === 'databricks-count' && (
        <div className="dp-sql">
          <code>{sql}</code>
        </div>
      )}

      {step.action && (
        <div className="dp-step-actions">
          <button type="button" className="btn" disabled={running || anyRunning} onClick={onRun}>
            {running ? 'Running…' : result ? 'Run again' : isCount ? 'Show query' : 'Run'}
          </button>
          {isCount && (
            <a
              className={`btn ghost ${status.databricksUrl ? '' : 'disabled'}`}
              href={status.databricksUrl ?? undefined}
              target="_blank"
              rel="noopener noreferrer"
              aria-disabled={!status.databricksUrl}
            >
              Open in Databricks ↗
            </a>
          )}
          {result && !waiting && (
            <span className={`dp-inline ${result.ok ? 'ok' : 'bad'}`}>
              {result.ok ? '✓' : '✗'} {result.message}
              {result.rows != null && !isCount && <b> ({result.rows} rows)</b>}
            </span>
          )}
        </div>
      )}

      {/* Money shots: the live-reveal row count for Databricks query steps, and the throughput
          comparison building up under each completed QoS load run. */}
      {isCount && result?.rows != null && <CountFlip value={result.rows} label="rows visible in Databricks" />}
      {step.phase === 'QOS' && step.action?.kind === 'runner' && result && qosRuns.length > 0 && (
        <QosChart runs={qosRuns} currentId={step.id} />
      )}

      {waiting && step.poll && (
        <div className="dp-wait">
          <span className="dp-wait-spinner" aria-hidden="true" />
          <span>
            {step.poll.waitingMessage} — re-checking in <b>{Math.max(waiting.secondsLeft, 0)}s</b>{' '}
            <span className="muted">(check {waiting.attempt}/{step.poll.maxAttempts})</span>
          </span>
          <button type="button" className="link dp-wait-cancel" onClick={onCancelWait}>
            Cancel
          </button>
        </div>
      )}
    </li>
  );
}
